/**
 * Standalone module for the `PaymentDialogContext` object.
 *
 * Kept separate from `upgrade-dialog.tsx` so that Vite/React Refresh hot
 * module reloads of the provider/consumer file do NOT replace the context
 * identity. When `createContext` lives in the same file as the Provider
 * + its hook, every HMR update mints a fresh context object — the
 * provider mounted in the tree keeps the OLD context, while re-rendered
 * consumers reach for the NEW one, so `useContext(...)` returns the
 * default `undefined` and `useUpgradeDialog` throws "must be used within
 * a PaymentDialogProvider" across the app until a hard refresh.
 *
 * Nothing in this file is exported except the context + its types, so
 * Vite will treat the module as HMR-stable.
 */

import type { CreditKind } from '@flashka/shared/credits';

import { createContext } from 'react';

export type PaymentDialogReason =
  | {
      reason: 'outOfCredits';
      /** The wallet that ran out, when the 402 body carried a kind flag.
       *  Absent → generic out-of-credits copy. */
      creditKind?: CreditKind;
      consecutiveDays?: number;
    }
  | {
      reason: 'premiumFeature';
      feature: 'ankiExport' | 'pdfSizeLimit' | 'multiSectionExpand';
    }
  | {
      reason: 'limitDialogUpgrade';
      creditKind: CreditKind;
    }
  | {
      reason: 'spontaneusFromUpgradeButton';
    }
  | {
      reason: 'switchPlanFromSettings';
    }
  | {
      /** Stripe Checkout's cancel_url landed back on the app. */
      reason: 'checkoutCancelled';
    };

export interface PaymentDialogContextType {
  showPaymentDialog: (reason: PaymentDialogReason) => void;
  closePaymentDialog: () => void;
}

export const PaymentDialogContext = createContext<PaymentDialogContextType | undefined>(
  undefined,
);
